import type { ReactNode } from 'react';

import { CDNImage } from '../CDNImage';
import { cn } from '../cn';
import { usePlatformLink } from '../contexts';
import { Files } from '../StrokeIcons';

export type DocumentCardProps = {
  /** Link href when card is clickable */
  href?: string;
  /** Image source URL */
  src?: string;
  /** Image alt text */
  alt: string;
  /** Additional content body */
  body?: string | ReactNode;
  /** View duration text */
  duration?: string;
  /** Date string */
  date?: string;
  className?: string;
};

const containerClassName = cn(
  'flex flex-row flex-1 gap-4 rounded-tr-lg rounded-br-lg rounded-bl-lg',
  'border border-gray-200 dark:border-gray-700',
  'bg-slate-50 dark:bg-slate-800',
  'px-2.5 py-1.5 no-underline',
  'transition-all duration-300 hover:shadow-md',
);

export function DocumentCard({ href, className, ...contentProps }: DocumentCardProps) {
  const Link = usePlatformLink();

  if (!href) {
    return (
      <div className={cn(containerClassName, className)}>
        <DocumentCardContent {...contentProps} />
      </div>
    );
  }

  return (
    <Link href={href} className={cn(containerClassName, className)}>
      <DocumentCardContent {...contentProps} />
    </Link>
  );
}

function DocumentCardContent({ src, alt, body, duration, date }: Omit<DocumentCardProps, 'href' | 'className'>) {
  const subtitle = [date, duration ? `viewed ${duration}` : null].filter(Boolean).join(' · ');
  return (
    <div className="m-0 flex flex-1 flex-col gap-1.5">
      <div className="flex flex-row gap-2">
        <div className="relative flex aspect-square h-full w-11 items-center justify-center overflow-hidden rounded-lg bg-gray-200 p-0.5 text-indigo-500 dark:bg-gray-700 [&>svg]:size-8">
          {src && <CDNImage src={src} alt={alt} fill style={{ objectFit: 'cover' }} />}
          {!src && <Files.File04 />}
        </div>
        <div className="flex flex-col">
          <p className="m-0 text-sm font-normal leading-5 text-gray-900 dark:text-gray-100">Viewed Content</p>
          {subtitle && <div className="truncate text-sm font-normal text-gray-500">{subtitle}</div>}
        </div>
      </div>
      {body != null && (
        <div className="rounded-sm bg-slate-100 p-2 text-xs leading-relaxed text-gray-500 dark:bg-slate-700/50">
          {body}
        </div>
      )}
    </div>
  );
}
